import React, { useState, useCallback, useImperativeHandle, forwardRef, useMemo, useEffect, useRef } from "react";
import {
  Grid,
  Column,
  Tile,
  Stack,
  Button,
  InlineLoading,
  ToastNotification,
  Form,
} from "@carbon/react";
import { callApi } from "../../../services/apiService";
import { Chat, Email, Image, Video,  } from "@carbon/icons-react";

const Gallery = forwardRef(({
  propertyUuid,
  onComplete,
  onSaved,
  onBack,
  onNavigateNext,
  currentStep = 5,
  CommonButtons,
  onSaveSuccess,
  attributeValues = [],
}, ref) => {
  //  STATE MANAGEMENT 
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState(false);
  
  // limits
  const MAX_PHOTO_SIZE_MB = 7;
  const MAX_VIDEO_SIZE_MB = 7;
  const MAX_PHOTOS_COUNT = 10;
  const MAX_VIDEOS_COUNT = 2;

  // Gallery states
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);

  const [deletingPhotos, setDeletingPhotos] = useState([]);
  const [deletingVideos, setDeletingVideos] = useState([]);

  // Preview modal state
  const [previewMedia, setPreviewMedia] = useState(null);

  // Notification timeouts
  const successTimeoutRef = useRef(null);
  const errorTimeoutRef = useRef(null);

  const uploadInProgress = useRef(false);
  const initializedPropertyRef = useRef(null);
  
  const MEDIA_TYPES = useMemo(() => {
    console.log('🔍 [MEDIA_TYPES] Computing media types from attributeValues:', attributeValues);
    
    const photoAttr = attributeValues?.find(attr => {
      const value = attr.value || attr.ATTRIBUTE_VALUE_ID;
      return (attr.attrName?.toLowerCase().includes('photo') || 
              attr.label?.toLowerCase().includes('photo') ||
              value === 882); 
    });
    
    const videoAttr = attributeValues?.find(attr => {
      const value = attr.value || attr.ATTRIBUTE_VALUE_ID;
      return (attr.attrName?.toLowerCase().includes('video') || 
              attr.label?.toLowerCase().includes('video') ||
              value === 883);
    });

const mediaTypes = {
  PHOTO:
    photoAttr?.value ||
    photoAttr?.ATTRIBUTE_VALUE_ID,

  VIDEO:
    videoAttr?.value ||
    videoAttr?.ATTRIBUTE_VALUE_ID,
};
    console.log('[MEDIA_TYPES] Final media types:', mediaTypes);
    return mediaTypes;
  }, [attributeValues]);

  // Helper to clear notifications
  const clearNotifications = useCallback(() => {
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
  }, []);

  const showSuccess = useCallback((message, timeout = 3000) => {
    clearNotifications();
    setSuccess(message);
    successTimeoutRef.current = setTimeout(() => setSuccess(""), timeout);
  }, [clearNotifications]);

  const showError = useCallback((message, timeout = 5000) => {
    clearNotifications();
    setError(message);
    errorTimeoutRef.current = setTimeout(() => setError(""), timeout);
  }, [clearNotifications]);

  //  normalize media 



const normalizeMediaItem = useCallback((item) => {
  const mediaUrl = item.MEDIA_URL || item.media_url || "";

  return {
    PROPERTY_MEDIA_ID: item.PROPERTY_MEDIA_ID || item.id,
    PROPERTY_ID: item.PROPERTY_ID || item.property_id,
    ATTRIBUTE_VALUE_ID:
      item.ATTRIBUTE_VALUE_ID || item.attribute_value_id,

    MEDIA_URL: mediaUrl,

    STATUS: item.STATUS || item.status,
    CREATED_TIMESTAMP:
      item.CREATED_TIMESTAMP || item.created_timestamp,
    UPDATED_TIMESTAMP:
      item.UPDATED_TIMESTAMP || item.updated_timestamp,
    property_media_type: item.property_media_type
  };
}, []);


// ========== FETCH PROPERTY MEDIA ==========
const fetchPropertyMedia = useCallback(async () => {
  if (!propertyUuid) return;

  try {
    console.log("🟢 Fetching gallery media for:", propertyUuid);

    const response = await callApi(
      "GET",
      `/property-media/?property_uuid=${propertyUuid}`
    );

    console.log("📥 Property media response:", response);

    const mediaData = Array.isArray(response?.data)
      ? response.data
      : response?.data
      ? [response.data]
      : [];

    console.log("📸 Media count:", mediaData.length);

    const photosData = mediaData.filter((item) => {
      const attrId = Number(
        item.ATTRIBUTE_VALUE_ID ||
        item.attribute_value_id
      );

      return attrId === Number(MEDIA_TYPES.PHOTO);
    });

    const videosData = mediaData.filter((item) => {
      const attrId = Number(
        item.ATTRIBUTE_VALUE_ID ||
        item.attribute_value_id
      );

      return attrId === Number(MEDIA_TYPES.VIDEO);
    });

    const normalizedPhotos = photosData.map((item) => ({
      ...normalizeMediaItem(item),
      is_youtube: false,
    }));

    const normalizedVideos = videosData.map((item) => ({
      ...normalizeMediaItem(item),
      is_youtube: false,
    }));

    console.log("🖼️ Updated Photos:", normalizedPhotos);
    console.log("🎥 Updated Videos:", normalizedVideos);

    setPhotos(normalizedPhotos);
    setVideos(normalizedVideos);

  } catch (err) {
    console.error("❌ Failed to load gallery:", err);
    showError("Failed to load existing media.");
  }
}, [
  propertyUuid,
  MEDIA_TYPES.PHOTO,
  MEDIA_TYPES.VIDEO,
  normalizeMediaItem,
  showError,
]);

// ========== DATA INITIALIZATION ==========
useEffect(() => {
  if (!propertyUuid) return;

  // Wait until media type IDs are available
  if (!MEDIA_TYPES.PHOTO && !MEDIA_TYPES.VIDEO) {
    console.log("⏳ Waiting for media types...");
    return;
  }

  // Prevent unnecessary initial fetch for same property
  if (initializedPropertyRef.current === propertyUuid) {
    return;
  }

  initializedPropertyRef.current = propertyUuid;

  fetchPropertyMedia();

}, [
  propertyUuid,
  MEDIA_TYPES.PHOTO,
  MEDIA_TYPES.VIDEO,
  fetchPropertyMedia,
]);


  // ========== FILE UPLOAD HANDLERS ==========
 const handlePhotoUpload = useCallback(
  async (event) => {
    const files = Array.from(event.target.files);

    console.log(
      "📤 [handlePhotoUpload] Files selected:",
      files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      }))
    );

    if (files.length === 0 || !propertyUuid) {
      if (!propertyUuid) {
        showError(
          "Please save property details first before uploading photos"
        );
      }
      return;
    }

    if (!MEDIA_TYPES.PHOTO) {
      showError("Photo media type is not available.");
      return;
    }

    if (uploadInProgress.current) {
      showError("Upload in progress. Please wait.");
      return;
    }

    // Check photo limit
    if (photos.length + files.length > MAX_PHOTOS_COUNT) {
      showError(`You can upload a maximum of ${MAX_PHOTOS_COUNT} photos.`);
      event.target.value = "";
      return;
    }

    uploadInProgress.current = true;
    setUploading(true);
    setError("");

    try {
      for (const file of files) {
        // File size validation
        if (file.size > MAX_PHOTO_SIZE_MB * 1024 * 1024) {
          showError(
            `Image "${file.name}" exceeds ${MAX_PHOTO_SIZE_MB}MB limit.`
          );
          continue;
        }

        console.log("📤 Uploading photo:", file.name);

        const formData = new FormData();

        formData.append("property_uuid", propertyUuid);
        formData.append(
          "attribute_value_id",
          String(MEDIA_TYPES.PHOTO)
        );
        formData.append("files", file);

        const uploadRes = await callApi(
          "POST",
          "/property-media/",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        console.log("📥 Upload response:", uploadRes);

        if (!uploadRes?.data) {
          throw new Error(
            uploadRes?.message ||
              `Failed to upload "${file.name}"`
          );
        }

        showSuccess(
          `Photo "${file.name}" uploaded successfully!`
        );
      }

      // 🔥 Fetch once after all uploads are complete
      await fetchPropertyMedia();

    } catch (err) {
      console.error(
        "❌ [handlePhotoUpload] Upload error:",
        err
      );

      showError(
        err?.message ||
          "Failed to upload photos. Please try again."
      );

    } finally {
      setUploading(false);
      uploadInProgress.current = false;

      // Reset file input
      event.target.value = "";

      console.log(
        "🏁 [handlePhotoUpload] Upload process completed"
      );
    }
  },
  [
    photos.length,
    propertyUuid,
    MEDIA_TYPES.PHOTO,
    showSuccess,
    showError,
    fetchPropertyMedia,
  ]
);

const handleVideoUpload = useCallback(
  async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0 || !propertyUuid) {
      if (!propertyUuid) {
        showError(
          "Please save property details first before uploading videos"
        );
      }
      return;
    }

    if (!MEDIA_TYPES.VIDEO) {
      showError("Video media type is not available.");
      return;
    }

    if (uploadInProgress.current) {
      showError("Upload in progress. Please wait.");
      return;
    }

    if (videos.length + files.length > MAX_VIDEOS_COUNT) {
      showError(
        `You can upload a maximum of ${MAX_VIDEOS_COUNT} videos.`
      );

      event.target.value = "";
      return;
    }

    uploadInProgress.current = true;
    setUploading(true);
    setError("");

    try {
      for (const file of files) {
        if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
          showError(
            `Video "${file.name}" exceeds ${MAX_VIDEO_SIZE_MB}MB limit.`
          );
          continue;
        }

        const formData = new FormData();

        formData.append("property_uuid", propertyUuid);
        formData.append(
          "attribute_value_id",
          String(MEDIA_TYPES.VIDEO)
        );
        formData.append("files", file);

        const uploadRes = await callApi(
          "POST",
          "/property-media/",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        console.log(
          "📥 Video upload response:",
          uploadRes
        );

        if (!uploadRes?.data) {
          throw new Error(
            uploadRes?.message ||
              `Failed to upload "${file.name}"`
          );
        }

        showSuccess(
          `Video "${file.name}" uploaded successfully!`
        );
      }

      // 🔥 Fetch videos again from GET API
      await fetchPropertyMedia();

    } catch (err) {
      console.error(
        "❌ [handleVideoUpload] Upload error:",
        err
      );

      showError(
        err?.message ||
          "Failed to upload videos. Please try again."
      );

    } finally {
      setUploading(false);
      uploadInProgress.current = false;
      event.target.value = "";
    }
  },
  [
    videos.length,
    propertyUuid,
    MEDIA_TYPES.VIDEO,
    showSuccess,
    showError,
    fetchPropertyMedia,
  ]
);

  const removeVideo = useCallback(async (videoId) => {
    if (!videoId) {
      showError("Video ID is required");
      return;
    }
    
    setDeletingVideos(prev => [...prev, videoId]);
    
    try {
      const response = await callApi("DELETE", `/property-media/${videoId}`);
      
      if (response?.status_code === 200 || response?.success === true) {
        const updatedVideos = videos.filter(v => v.PROPERTY_MEDIA_ID !== videoId);
        setVideos(updatedVideos);
        
        if (onComplete) {
          onComplete({ 
            photos, 
            videos: updatedVideos
          }, false);
        }
        
        showSuccess("Video deleted successfully!");
      } else {
        throw new Error(response?.message || "Video delete failed");
      }
      
    } catch (err) {
      console.error("Video delete error:", err);
      showError(err.message || "Failed to delete video");
    } finally {
      setDeletingVideos(prev => prev.filter(id => id !== videoId));
    }
  }, [videos, photos, onComplete, showSuccess, showError]);
  // ========== REMOVE PHOTO ==========
const removePhoto = useCallback(async (photoId) => {
  if (!photoId) {
    showError("Photo ID is required");
    return;
  }

  setDeletingPhotos((prev) => [...prev, photoId]);

  try {
    const response = await callApi(
      "DELETE",
      `/property-media/${photoId}`
    );

    console.log("Delete photo response:", response);

    if (
      response?.status_code === 200 ||
      response?.success === true ||
      response?.status === 200
    ) {
      const updatedPhotos = photos.filter(
        (photo) => photo.PROPERTY_MEDIA_ID !== photoId
      );

      setPhotos(updatedPhotos);

      if (onComplete) {
        onComplete(
          {
            photos: updatedPhotos,
            videos,
          },
          false
        );
      }

      showSuccess("Photo deleted successfully!");

    } else {
      throw new Error(
        response?.message || "Failed to delete photo"
      );
    }

  } catch (err) {
    console.error("❌ Delete photo error:", err);

    showError(
      err?.message || "Failed to delete photo"
    );

  } finally {
    setDeletingPhotos((prev) =>
      prev.filter((id) => id !== photoId)
    );
  }
}, [
  photos,
  videos,
  onComplete,
  showSuccess,
  showError,
]);

  // ========== VALIDATION ==========
  const hasValidationErrors = useCallback(() => {
    return false;
  }, []);

  // ========== SAVE HANDLER ==========
  const handleSave = useCallback(async (navigateNext = false) => {
    if (hasValidationErrors()) {
      showError("Please check your data and try again.");
      return { success: false, error: "Invalid data" };
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const galleryData = {
        photos,
        videos,
      };

      // Use parent's save success handler
      if (onSaveSuccess && propertyUuid) {
        onSaveSuccess(propertyUuid, galleryData, currentStep);
      }

      // Show success message
      const hasUpdates = photos.length > 0 || videos.length > 0;
      if (hasUpdates) {
        showSuccess("Gallery updated successfully!");
      }

      // Navigate if requested
      if (navigateNext && onNavigateNext) {
        onNavigateNext(galleryData);
      }

      return {
        success: true,
        galleryData
      };

    } catch (err) {
      let errorMessage = "Failed to save gallery data. Please try again.";
      if (err?.status_code === 422) {
        errorMessage = "Validation Error: Please check your data and try again.";
      } else if (err?.message) {
        errorMessage = err.message;
      }
      showError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  }, [hasValidationErrors, photos, videos, onSaveSuccess, propertyUuid, currentStep, onNavigateNext, showSuccess, showError]);

  // ========== REF EXPOSURE FOR PARENT ==========
  useImperativeHandle(ref, () => ({
    handleSave: () => handleSave(false),
  }));

  // ========== BUTTON HANDLERS ==========
  const handleSaveOnly = useCallback(async (e) => {
    e?.preventDefault?.();
    await handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback(async (e) => {
    e?.preventDefault?.();
    const result = await handleSave(true);
    
    if (result?.success) {
      if (onComplete) {
        onComplete(result.galleryData, true);
      } else if (onSaved) {
        onSaved(propertyUuid, result.galleryData, true);
      }
    }
  }, [handleSave, onComplete, onSaved, propertyUuid]);

  const handleBack = useCallback((e) => {
    e?.preventDefault?.();
    if (onBack) onBack();
  }, [onBack]);

  // ========== PREVIEW MODAL COMPONENT ==========
  const PreviewModal = () => {
    if (!previewMedia) return null;
    
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.8)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer"
      }} onClick={() => setPreviewMedia(null)}>
        <div style={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          backgroundColor: "white",
          borderRadius: "8px",
          overflow: "hidden",
          cursor: "default"
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#f4f4f4",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span style={{ fontWeight: "bold" }}>
              {previewMedia.type === 'image' && 'Image Preview'}
              {previewMedia.type === 'video' && 'Video Preview'}
            </span>
            <button
              onClick={() => setPreviewMedia(null)}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                cursor: "pointer",
                color: "#666"
              }}
            >
              ×
            </button>
          </div>
          <div style={{ padding: "1rem", textAlign: "center" }}>
            {previewMedia.type === 'image' && (
              <img 
                src={previewMedia.url} 
                alt="Preview" 
                style={{ 
                  maxWidth: "100%", 
                  maxHeight: "70vh", 
                  objectFit: "contain" 
                }} 
              />
            )}
            
            {previewMedia.type === 'video' && (
              <video 
                controls 
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
                src={previewMedia.url}
              >
                Your browser does not support video playback.
              </video>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== UI COMPONENTS ==========
  const PhotoGallery = useCallback(() => (
    <div style={{ marginBottom: "2rem" }}>
      <Tile>
        <h4 style={{ marginBottom: "1rem" }}>📸 Property Photos</h4>

        <div style={{
          fontSize: "0.875rem",
          color: "#6f6f6f",
          background: "#f4f4f4",
          padding: "0.75rem 1rem",
          borderRadius: "6px",
          marginBottom: "1.5rem"
        }}>
          <strong>Upload Guidelines:</strong><br />
          • Images allowed: JPG, JPEG, PNG<br />
          • Max size: {MAX_PHOTO_SIZE_MB} MB per image<br />
          • Max photos: {MAX_PHOTOS_COUNT} images
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <input
            type="file"
            id="photo-upload"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: "none" }}
            disabled={!propertyUuid}
          />

          <Button
            kind="secondary"
            size="lg"
            onClick={() => document.getElementById("photo-upload").click()}
            disabled={
              uploading ||
              saving ||
              !propertyUuid ||
              photos.length >= MAX_PHOTOS_COUNT
            }
          >
            {uploading ? <InlineLoading /> : "📷 Add Photos"}
          </Button>

          {!propertyUuid && (
            <div style={{ fontSize: "0.875rem", color: "#da1e28", marginTop: "0.5rem" }}>
              Please save property details first before uploading photos
            </div>
          )}

          <div style={{ fontSize: "0.875rem", color: "#525252", marginTop: "0.5rem" }}>
            Photos added ({photos.length}/{MAX_PHOTOS_COUNT})
          </div>
        </div>

        {photos.length > 0 ? (
          <div>
            <p><strong>Uploaded Photos:</strong></p>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: "1rem",
              marginTop: "1rem"
            }}>
              {photos.map((photo) => {
                const isDeleting = deletingPhotos.includes(photo.PROPERTY_MEDIA_ID);
                const imageUrl = photo.MEDIA_URL || photo.media_url || "";

console.log("🖼️ Admin Gallery Image:", {
  imageUrl,
});

                return (
                  <div
                    key={photo.PROPERTY_MEDIA_ID}
                    style={{
                      position: "relative",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "0.5rem",
                      backgroundColor: "#fafafa"
                    }}
                  >
                    {imageUrl ? (
                      <div style={{ position: "relative", cursor: "pointer" }} onClick={() => {
  if (imageUrl) {
    setPreviewMedia({
      type: "image",
      url: imageUrl,
    });
  }
}}>
<img
  src={imageUrl}
  alt="Property"
  style={{
    width: "100%",
    height: "120px",
    objectFit: "cover",
    borderRadius: "4px",
    display: "block",
  }}
  onLoad={() => {
  console.log("✅ Image loaded:", imageUrl);
}}
  onError={(e) => {
    console.error("❌ Image failed to load:", imageUrl);
    e.currentTarget.style.display = "none";
  }}
/>
                        <div style={{
                          position: "absolute",
                          bottom: "5px",
                          right: "5px",
                          background: "rgba(0,0,0,0.6)",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}>
                          <Image size={14} style={{ color: "white" }} />
                          <span style={{ color: "white", fontSize: "11px" }}>Photo</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        width: "100%",
                        height: "120px",
                        backgroundColor: "#f0f0f0",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        color: "#666",
                        gap: "8px"
                      }}>
                        <Image size={32} />
                        <span style={{ fontSize: "12px" }}>No Image</span>
                      </div>
                    )}

                    <button
                      onClick={() => removePhoto(photo.PROPERTY_MEDIA_ID)}
                      disabled={saving || isDeleting}
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        background: "#ff4d4f",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        cursor: saving || isDeleting ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: saving || isDeleting ? 0.6 : 1
                      }}
                      title="Remove photo"
                    >
                      {isDeleting ? "⌛" : "×"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: "center",
            padding: "2rem",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            color: "#666"
          }}>
            <Image size={48} style={{ marginBottom: "1rem", opacity: 0.5 }} />
            <p>No photos uploaded yet</p>
            <p style={{ fontSize: "0.875rem" }}>Click "Add Photos" to upload property images</p>
          </div>
        )}
      </Tile>
    </div>
  ), [
  photos,
  uploading,
  saving,
  propertyUuid,
  handlePhotoUpload,
  removePhoto,
  deletingPhotos
]);

  const VideoGallery = useCallback(() => (
    <div style={{ marginBottom: "2rem" }}>
      <Tile>
        <h4 style={{ marginBottom: "1rem" }}>🎥 Property Videos</h4>
        <div style={{ 
          fontSize: "0.875rem", 
          color: "#6f6f6f",
          background: "#f4f4f4",
          padding: "0.75rem 1rem",
          borderRadius: "6px",
          marginBottom: "1.5rem"
        }}>
          <strong>Upload Guidelines:</strong><br/>
          • Videos allowed: MP4, MOV, AVI<br/>
          • Max size: {MAX_VIDEO_SIZE_MB} MB per video<br/>
          • Max videos: {MAX_VIDEOS_COUNT} files
        </div>

        <div style={{ marginBottom: "2rem" }}>
          <input
            type="file"
            id="video-upload"
            multiple
            accept="video/*"
            onChange={handleVideoUpload}
            style={{ display: "none" }}
            disabled={!propertyUuid}
          />
          <Button
            kind="secondary"
            onClick={() => document.getElementById('video-upload').click()}
            disabled={uploading || saving || !propertyUuid || videos.length >= MAX_VIDEOS_COUNT}
            size="lg"
          >
            {uploading ? <InlineLoading /> : "🎥 Upload Video Files"}
          </Button>
          {!propertyUuid && (
            <div style={{ fontSize: "0.875rem", color: "#da1e28", marginTop: "0.5rem" }}>
              Please save property details first before uploading videos
            </div>
          )}
          <div style={{ fontSize: "0.875rem", color: "#525252", marginTop: "0.5rem" }}>
            {videos.length}/{MAX_VIDEOS_COUNT} video file(s) uploaded
          </div>
        </div>

        {videos.length > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <p><strong>Uploaded Video Files:</strong></p>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
              {videos.map((video) => {
                const isDeleting = deletingVideos.includes(video.PROPERTY_MEDIA_ID);
                const videoUrl = video.MEDIA_URL || video.media_url || "";
                
                return (
                  <div key={video.PROPERTY_MEDIA_ID} style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between",
                    padding: "1rem",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                    cursor: "pointer"
                  }} onClick={() => setPreviewMedia({ type: 'video', url: videoUrl })}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", flex: 1 }}>
                      <Video size={24} style={{ color: "#0066cc" }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "500", wordBreak: "break-all" }}>
                          {videoUrl.split('/').pop() || 'Video File'}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#525252" }}>
                          Type: Video File
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeVideo(video.PROPERTY_MEDIA_ID);
                      }}
                      disabled={saving || isDeleting}
                      style={{
                        background: isDeleting ? "#ff9500" : "#ff4d4f",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "0.5rem 1rem",
                        cursor: (saving || isDeleting) ? "not-allowed" : "pointer",
                        fontSize: "0.875rem",
                        opacity: (saving || isDeleting) ? 0.6 : 1,
                        marginLeft: "1rem"
                      }}
                    >
                      {isDeleting ? "Deleting..." : "Remove"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Tile>
    </div>
  ), [videos, uploading, saving, propertyUuid, handleVideoUpload, removeVideo, deletingVideos]);

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999, minWidth: "300px" }}
          timeout={5000}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999, minWidth: "300px" }}
          timeout={3000}
        />
      )}

      <PreviewModal />

      <div style={{ backgroundColor: "white" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
              <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                <h3 style={{ marginBottom: "0.5rem", fontWeight: "600", color: "#161616" }}>
                  Property Gallery
                </h3>
                <p style={{ color: "#525252" }}>
                  Upload photos and videos to showcase your property
                </p>
              </div>

              <Form>
                <Stack gap={3}>
                  <PhotoGallery />
                
                  <div 
                    style={{
                      margin: "2rem 0",
                      padding: "1.5rem",
                      background: "#f4f4f4",
                      borderRadius: "1px",
                      textAlign: "center"
                    }}
                  >
                    <h4 style={{ margin: 0, marginBottom: "1rem" }}>
                      Need help uploading photos?
                    </h4>
                    <p style={{ marginBottom: "1rem", color: "#525252" }}>
                      Our team can assist you with uploading property photos
                    </p>

                    <div 
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        gap: "0.75rem", 
                        marginBottom: "0.75rem" 
                      }}
                    >
                      <Chat size={24} />
                      <span style={{ fontSize: "1rem", fontWeight: "500" }}>0-9241-700-000</span>
                    </div>

                    <div 
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        gap: "0.75rem" 
                      }}
                    >
                      <Email size={24} />
                      <span style={{ fontSize: "1rem", fontWeight: "500" }}>photos@Sellvam.in</span>
                    </div>
                  </div>

                  <VideoGallery />

                  {CommonButtons ? (
                    <CommonButtons
                      onBack={handleBack}
                      onSave={handleSaveOnly}
                      onNext={handleSaveAndNext}
                      showBack={true}
                      showSave={true}
                      showNext={true}
                      isSaveLoading={saving || uploading}
                      stepData={{
                        photos,
                        videos,
                      }}
                    />
                  ) : (
                    <div style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center",
                      marginTop: "2rem",
                      paddingTop: "1rem",
                      borderTop: "1px solid #e0e0e0"
                    }}>
                      <Button
                        kind="secondary"
                        onClick={handleBack}
                        disabled={saving || uploading}
                        size="lg"
                        style={{ minWidth: '120px' }}
                      >
                        Back
                      </Button>

                      <div style={{ display: "flex", gap: "1rem" }}>
                        <Button
                          kind="secondary"
                          onClick={handleSaveOnly}
                          disabled={saving || uploading || hasValidationErrors()}
                          size="lg"
                          style={{ minWidth: '120px' }}
                        >
                          {saving ? <InlineLoading /> : "Save"}
                        </Button>

                        <Button
                          kind="primary"
                          onClick={handleSaveAndNext}
                          disabled={saving || uploading || hasValidationErrors()}
                          size="lg"
                          style={{ minWidth: '140px' }}
                        >
                          {saving ? <InlineLoading /> : "Next Step"}
                        </Button>
                      </div>
                    </div>
                  )}
                </Stack>
              </Form>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
});

export default Gallery;